import React, { useMemo } from 'react'
import styled from 'styled-components'
import { Flex, ResponsiveSVG } from 'ui/acro'
import Lock from '../../../assets/images/lock.svg'
import Unlock from '../../../assets/images/unlock.svg'
import { showLabel } from '../../components_v1/SchemaSectionRenderer/common/components/utils'
import {
  ALO_ACCESS,
  PREMIUM,
  LIMITED,
  hasAloAccessMembership
} from '../../components_v1/SchemaSectionRenderer/common/components/constants'
import {
  premium_unlock_click_event,
  verify_account_initiated_event
} from 'gtm/pushEvents'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-left: auto;
  margin-right: auto;
  width: 35%;
  border-radius: 4px;
  border: ${({ variant }) =>
    [ALO_ACCESS, LIMITED].includes(variant)
      ? '1px solid #d6c7b3'
      : '1px solid #3A3A39'};
  background: ${({ variant }) =>
    [ALO_ACCESS, LIMITED].includes(variant) ? '#f4f1ec' : '#CDCDCD'};
  padding: 12px 16px;
  margin-top: 12px;

  @media (max-width: 768px) {
    width: calc(100% - 32px);
    margin: 12px 16px;
  }
`

const BudgeContainer = styled.div`
  text-align: center;
  position: relative;
  @media (max-width: 768px) {
    text-align: left;
  }
`

const TextContainer = styled.div`
  color: #000;
  text-align: center;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.352px;
  @media (max-width: 768px) {
    text-align: left;
  }
`

const ContainerSvg = styled(Flex)`
  background-color: ${({ variant }) =>
    [ALO_ACCESS, LIMITED].includes(variant) ? '#D6C7B3' : '#3A3A39'};
  color: ${({ variant }) =>
    [ALO_ACCESS, LIMITED].includes(variant) ? '#000' : '#FFF'};
  padding: 4px 8px;
  justify-content: flex-end;
  align-items: center;
  gap: 2px;
  font-family: 'Proxima-Nova';
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 14px;
  border-radius: 12px;
  display: inline-flex;

  svg {
    fill: ${({ variant }) =>
      [ALO_ACCESS, LIMITED].includes(variant) ? '#000' : '#fff'};
    width: 24px;
    height: 24px;
  }
`

const Link = styled('a')({
  color: '#000',
  textDecoration: 'underline',
  fontFamily: 'Proxima-Nova',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: 700,
  lineHeight: '24px',
  letterSpacing: '0.352px',
  '&:hover, &:active, &:visited': {
    color: '#000'
  }
})

const SignupContainer = styled.div`
  text-align: center;
  @media (max-width: 768px) {
    text-align: left;
  }
`

const SeriesAccessIdentifier = ({ user, product }) => {
  const availabilityGating = product?.availabilityGating
  const membershipTier = user?.membershipTier ?? ''

  const identifierLabel = {
    ALO_ACCESS: 'ALO Access',
    LIMITED: 'Limited',
    PREMIUM: 'Premium'
  }[availabilityGating]

  const identifierText = {
    ALO_ACCESS: user
      ? 'Free AWC access is powered by the ALO Access loyalty program. Visit Manage Membership and join or link to unlock the full AWC library.'
      : 'Unlock access to the full content library for free by signing up or sign in using ALO account.',
    LIMITED: hasAloAccessMembership(membershipTier)
      ? 'You have limited access to this program with ALO Access. Unlock the rest of the program by upgrading to premium with a monthly subscription plan.'
      : 'Join now by signing up or sign in using ALO account to unlock part of this program at no cost.',
    PREMIUM: hasAloAccessMembership(membershipTier)
      ? 'Unlock this program by upgrading to premium with a monthly subscription plan.'
      : 'Join now at no cost and upgrade later to unlock this program with premium.'
  }[availabilityGating]

  const REDIRECT_URL = useMemo(() => {
    if (user) {
      return `/settings/membership?redirect_to=${window.location.pathname}`
    }
    return `/signin?redirect_to=${encodeURIComponent(window.location.pathname)}`
  }, [user, membershipTier])

  const handleClick = () => {
    if (user) {
      return premium_unlock_click_event('series')
    }
    verify_account_initiated_event('series_details_visitor')
  }

  if (!availabilityGating) return null
  if (![ALO_ACCESS, PREMIUM, LIMITED].includes(availabilityGating)) return null
  return (
    <>
      {showLabel(membershipTier, availabilityGating) && (
        <Container variant={availabilityGating}>
          <BudgeContainer>
            <ContainerSvg variant={availabilityGating}>
              <ResponsiveSVG
                SVG={
                  availabilityGating === LIMITED &&
                  hasAloAccessMembership(membershipTier)
                    ? Unlock
                    : Lock
                }
                width='14px'
                height='14px'
              />
              {identifierLabel}
            </ContainerSvg>
          </BudgeContainer>
          <TextContainer>{identifierText}</TextContainer>
          <SignupContainer>
            <Link href={REDIRECT_URL} target='_self' onClick={handleClick}>
              {user ? 'Manage Membership' : 'JOIN NOW'}
            </Link>
          </SignupContainer>
        </Container>
      )}
    </>
  )
}

export default SeriesAccessIdentifier
